/**
 * Pure dedupe gate for `<PersonalizationRefreshTrigger />` (jam-4 MED,
 * 2026-09-04): every DOCUMENT load for a signed-in shopper fired TWO
 * identical `/resources/personalization-refresh` POSTs ~150–400ms apart —
 * the `pzRefreshWanted` loader-signal trigger AND the `analytics.js`
 * identify() trigger both fired, each behind its own private latch. (SPA
 * navs fired one, because identify doesn't re-fire there.)
 *
 * One shared gate, two entry points, preserving exactly-one-per-trigger
 * semantics:
 *   - WANTED trigger: at most one fire per navigation (`locationKey`),
 *     COUNTING a fire the identify trigger already made for that navigation.
 *   - IDENTIFY trigger: fires only when the identity it announces DIFFERS
 *     from the identity the last refresh ran under — which keeps the §1.7
 *     pre-identify self-heal (wanted fired as anonymous, identify lands with
 *     a fresh userId ⇒ refresh again under the new identity) while killing
 *     the jam-4 dup (returning shopper: `ajs_user_id` was already on the
 *     wanted fire's request, identify announces the SAME id ⇒ skip). A
 *     mid-session account switch (identify with a DIFFERENT userId) fires
 *     again by the same rule.
 *
 * `lastFiredUserId` is the client-readable `ajs_user_id` at fire time (the
 * ajs cookies are analytics.js's own, never HttpOnly); `null` = the refresh
 * ran anonymous.
 */

export interface RefreshGateState {
  /** `location.key` of the last fire — one refresh per navigation max. */
  firedForKey: string | null;
  /** Segment userId the last fire ran under (`null` = anonymous). */
  lastFiredUserId: string | null;
}

export const INITIAL_REFRESH_GATE: RefreshGateState = {
  firedForKey: null,
  lastFiredUserId: null,
};

/** The `pzRefreshWanted` loader-signal trigger (one attempt per navigation). */
export function gateWantedRefresh(
  state: RefreshGateState,
  args: { locationKey: string; currentUserId: string | null },
): { fire: boolean; next: RefreshGateState } {
  if (state.firedForKey === args.locationKey) {
    return { fire: false, next: state };
  }
  return {
    fire: true,
    next: {
      firedForKey: args.locationKey,
      lastFiredUserId: args.currentUserId,
    },
  };
}

/** The `analytics.on("identify")` trigger (fires on identity CHANGE only). */
export function gateIdentifyRefresh(
  state: RefreshGateState,
  args: { locationKey: string; userId: unknown },
): { fire: boolean; next: RefreshGateState } {
  if (typeof args.userId !== "string" || !args.userId) {
    return { fire: false, next: state };
  }
  if (args.userId === state.lastFiredUserId) {
    return { fire: false, next: state };
  }
  return {
    fire: true,
    next: { firedForKey: args.locationKey, lastFiredUserId: args.userId },
  };
}
